import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import Logo from "../../components/Logo";
import ThemeToggle from "../../components/ThemeToggle";
import LoadingSpinner from "../../components/LoadingSpinner";
import api from "../../api/client";
import { Link2 } from "lucide-react";
import EmployerNavBar from "../../components/EmployerNavBar";

export default function EmployerDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const incomingToken =
    searchParams.get("connect") ||
    sessionStorage.getItem("joborigo_connect_token");

  const { employerUser, employerToken, clearEmployerAuth } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [createdKey, setCreatedKey] = useState(null);
  const [loading, setLoading] = useState(true);

  const [connectToken, setConnectToken] = useState(() => {
    const token =
      searchParams.get("connect") ||
      sessionStorage.getItem("joborigo_connect_token");
    if (token) sessionStorage.removeItem("joborigo_connect_token");
    return token ?? "";
  });
  const [connecting, setConnecting] = useState(false);
  const [connectResult, setConnectResult] = useState(null);
  const [previewState, setPreviewState] = useState(null);
  const [copiedId, setCopiedId] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);

  useEffect(() => {
    document.title = employerUser?.is_agency
      ? "Joborigo: Agency Dashboard"
      : "Joborigo: Employer Dashboard";
  }, [employerUser]);

  useEffect(() => {
    if (incomingToken) {
      setTimeout(() => {
        document
          .getElementById("connect-section")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
    }
  }, [incomingToken]);

  const authConfig = {
    headers: { Authorization: `Bearer ${employerToken}` },
    withCredentials: true,
  };

  const fetchData = async () => {
    try {
      const [statsRes, keysRes] = await Promise.all([
        api.get("/employer/dashboard", authConfig),
        api.get("/employer/api-keys", authConfig),
      ]);
      setStats(statsRes.data);
      setApiKeys(keysRes.data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateKey = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;
    try {
      const { data } = await api.post(
        "/employer/api-keys",
        { name: newKeyName },
        authConfig,
      );
      setCreatedKey(data);
      setNewKeyName("");
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail ?? err.message);
    }
  };

  const handleRevokeKey = async () => {
    if (!revokeTarget) return;
    try {
      await api.delete(`/employer/api-keys/${revokeTarget}`, authConfig);
      setRevokeTarget(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail ?? err.message);
    }
  };

  const handlePreview = async () => {
    if (!connectToken.trim()) return;
    setPreviewState("loading");
    setConnectResult(null);
    try {
      const { data } = await api.post(
        "/employer/integrate-ui/preview",
        { connect_token: connectToken.trim() },
        authConfig,
      );
      setPreviewState(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg).join(", ")
            : "Could not preview token";
      setConnectResult({ success: false, message });
      setPreviewState(null);
    }
  };

  const handleConfirmConnect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post(
        "/employer/integrate-ui",
        { connect_token: connectToken.trim() },
        authConfig,
      );
      setConnectResult({
        success: true,
        applicationId: data.application_id,
      });
      setConnectToken("");
      setPreviewState(null);
      navigate("/employers/dashboard", { replace: true });
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg).join(", ")
            : "Connection failed";
      setConnectResult({ success: false, message });
      setPreviewState(null);
    } finally {
      setConnecting(false);
    }
  };

  const handleCancelPreview = () => {
    setPreviewState(null);
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(connectResult.applicationId);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2500);
  };

  const sanitizeToken = (input) => {
    const trimmed = input.trim();
    const match = trimmed.match(/\/connect\/([^/?#]+)/);
    return match ? match[1] : trimmed;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100">
      <EmployerNavBar />

      <main className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold">
            {employerUser?.is_agency
              ? "Agency Dashboard"
              : "Employer Dashboard"}
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            {employerUser?.is_agency
              ? "Manage your API keys and candidate connections"
              : "Manage your API keys and candidate integrations"}
          </p>
        </div>

        {/* Connect token banner */}
        {incomingToken && !connectResult?.success && (
          <div className="mb-6 bg-brand-violet-tint dark:bg-brand-violet/10 border border-brand-violet/30 dark:border-brand-violet-on-dark/30 rounded-2xl px-5 py-4 flex items-start gap-3">
            <Link2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-brand-violet dark:text-brand-violet-on-dark" />
            <div>
              <p className="text-sm font-semibold text-brand-violet dark:text-brand-violet-on-dark mb-1">
                You have a candidate connect token
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                A candidate shared this link with you. Scroll down to "Connect a
                candidate" to link them to your account, then push real-time
                status updates directly to their Joborigo dashboard.
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center text-gray-400 py-20">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {[
                {
                  label: "Candidates linked",
                  value: stats?.total_integrations ?? 0,
                },
                {
                  label: "Status pushes",
                  value: stats?.status_pushes_total ?? 0,
                },
                {
                  label: "Active API keys",
                  value: stats?.active_api_keys ?? 0,
                },
              ].map((s) => (
                <div
                  key={s.label}
                  className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-5"
                >
                  <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">
                    {s.label}
                  </p>
                  <p className="text-3xl font-bold">{s.value}</p>
                </div>
              ))}
            </div>

            {/* Created key banner */}
            {createdKey && (
              <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-5">
                <p className="font-semibold text-green-700 dark:text-green-400 mb-2">
                  API key created. Copy it now, it will not be shown again
                </p>
                <div className="flex items-center gap-3">
                  <code className="flex-1 bg-white dark:bg-brand-dark-bg border border-green-200 dark:border-green-800 rounded-lg px-4 py-2.5 text-sm font-mono text-green-700 dark:text-green-400 break-all">
                    {createdKey.raw_key}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(createdKey.raw_key);
                      setCopiedKey(true);
                      setTimeout(() => setCopiedKey(false), 2500);
                    }}
                    className="flex-shrink-0 text-sm text-green-700 dark:text-green-400 border border-green-300 dark:border-green-700 px-3 py-2 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition"
                  >
                    {copiedKey ? "Copied!" : "Copy"}
                  </button>
                </div>
                <button
                  onClick={() => setCreatedKey(null)}
                  className="text-xs text-green-600 dark:text-green-500 mt-3 hover:underline"
                >
                  I have saved it, dismiss
                </button>
              </div>
            )}

            {/* Connect a candidate */}
            <div
              id="connect-section"
              className={`bg-white dark:bg-brand-dark-surface rounded-2xl border p-6 mb-6 transition ${
                incomingToken && !connectResult?.success
                  ? "border-brand-violet/40 dark:border-brand-violet-on-dark/40 ring-2 ring-brand-violet/20 dark:ring-brand-violet-on-dark/20"
                  : "border-gray-100 dark:border-white/10"
              }`}
            >
              <div className="flex items-start justify-between mb-1">
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  Connect a candidate
                </h2>
                {incomingToken && !connectResult?.success && (
                  <span className="text-xs font-medium bg-brand-violet text-white px-2 py-0.5 rounded-full">
                    Token ready
                  </span>
                )}
              </div>
              <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                If a candidate shared a connect link with you, paste their token
                here to link them to your account. Once connected you can push
                real-time status updates to their Joborigo dashboard via the API. No more silence, no more ghosting.
              </p>

              {/* Success state — show application_id for status pushes */}
              {connectResult?.success && (
                <div className="rounded-xl px-4 py-4 mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                  <p className="text-sm font-semibold text-green-700 dark:text-green-400 mb-2">
                    ✓ Candidate connected successfully
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                    Use this{" "}
                    <code className="bg-gray-100 dark:bg-brand-dark-bg px-1 rounded">
                      application_id
                    </code>{" "}
                    to push status updates to this candidate via the API:
                  </p>
                  <div className="flex items-center gap-2 mb-3">
                    <code className="flex-1 bg-white dark:bg-brand-dark-bg border border-green-200 dark:border-green-800 rounded-lg px-3 py-2 text-xs font-mono text-green-700 dark:text-green-400 break-all">
                      {connectResult.applicationId}
                    </code>
                    <button
                      onClick={handleCopyId}
                      className={`flex-shrink-0 text-xs px-3 py-2 rounded-lg border transition ${
                        copiedId
                          ? "border-green-300 text-green-600 bg-green-100 dark:bg-green-900/30"
                          : "border-green-300 dark:border-green-700 text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30"
                      }`}
                    >
                      {copiedId ? "Copied!" : "Copy"}
                    </button>
                  </div>
                  <div className="bg-gray-50 dark:bg-brand-dark-bg rounded-lg px-3 py-2">
                    <p className="text-xs text-gray-400 mb-1">
                      Example status push:
                    </p>
                    <code className="text-xs text-gray-500 dark:text-gray-400 break-all">
                      {`{ "application_id": "${connectResult.applicationId}", "status": "Interview", "note": "Scheduled for Monday" }`}
                    </code>
                  </div>
                </div>
              )}

              {/* Error state */}
              {connectResult && !connectResult.success && (
                <div className="rounded-xl px-4 py-3 mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {connectResult.message}
                  </p>
                </div>
              )}

              {!connectResult?.success && (
                <>
                  {/* State 1: No preview yet — show input + Preview button */}
                  {!previewState && (
                    <div className="flex gap-2">
                      <input
                        value={connectToken}
                        onChange={(e) =>
                          setConnectToken(sanitizeToken(e.target.value))
                        }
                        placeholder="Paste connect token here..."
                        className="flex-1 border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 bg-white dark:bg-brand-dark-surface text-sm"
                      />
                      <button
                        onClick={handlePreview}
                        disabled={!connectToken.trim()}
                        className="px-4 py-2 rounded-lg bg-brand-violet text-white text-sm font-medium disabled:opacity-50 hover:opacity-90 transition"
                      >
                        Preview
                      </button>
                    </div>
                  )}

                  {/* State 2: Loading preview */}
                  {previewState === "loading" && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Verifying token...
                    </p>
                  )}

                  {/* State 3: Preview ready — show confirmation */}
                  {previewState && previewState !== "loading" && (
                    <div className="border border-brand-violet/30 dark:border-brand-violet-on-dark/30 bg-brand-violet/5 dark:bg-brand-violet/10 rounded-lg p-4">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        Confirm connection
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                        Candidate{" "}
                        <span className="font-mono font-semibold text-brand-violet dark:text-brand-violet-on-dark">
                          {previewState.candidate_initial}.
                        </span>{" "}
                        · {previewState.job_title}
                        {previewState.company
                          ? ` at ${previewState.company}`
                          : ""}
                      </p>
                      {previewState.applied_at && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          Applied {previewState.applied_at.split("T")[0]}
                        </p>
                      )}
                      {previewState.already_connected && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
                          Already connected to your account.
                        </p>
                      )}
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-3 mb-4">
                        Confirming will use up this one-time connect token.
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleConfirmConnect}
                          disabled={connecting}
                          className="px-4 py-2 rounded-lg bg-brand-violet text-white text-sm font-medium disabled:opacity-50 hover:opacity-90 transition"
                        >
                          {connecting ? "Connecting..." : "Confirm connection"}
                        </button>
                        <button
                          onClick={handleCancelPreview}
                          disabled={connecting}
                          className="px-4 py-2 rounded-lg border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* API Keys */}
            <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-6 mb-6">
              <h2 className="font-semibold text-gray-900 dark:text-white mb-4">
                API Keys
              </h2>
              <form onSubmit={handleCreateKey} className="flex gap-3 mb-5">
                <input
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="Key name (e.g. Production)"
                  className="flex-1 border border-gray-200 dark:border-white/10 bg-white dark:bg-brand-dark-bg rounded-lg px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-violet dark:focus:ring-brand-violet-on-dark"
                />
                <button
                  type="submit"
                  className="bg-brand-violet text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:opacity-90 transition"
                >
                  Generate key
                </button>
              </form>

              {apiKeys.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-4">
                  No API keys yet. Create one above.
                </p>
              ) : (
                <div className="flex flex-col gap-3">
                  {[...apiKeys]
                    .sort((a, b) => b.is_active - a.is_active)
                    .map((key) => (
                      <div
                        key={key.id}
                        className="flex items-center justify-between gap-4 p-4 bg-gray-50 dark:bg-brand-dark-bg rounded-xl"
                      >
                        <div>
                          <p className="font-medium text-sm">{key.name}</p>
                          <p className="text-xs text-gray-400 font-mono mt-0.5">
                            {key.key_prefix}•••••••••••••••••••
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            Created{" "}
                            {new Date(key.created_at).toLocaleDateString(
                              "en-SE",
                            )}
                            {key.last_used_at &&
                              ` · Last used ${new Date(key.last_used_at).toLocaleDateString("en-SE")}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              key.is_active
                                ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {key.is_active ? "Active" : "Revoked"}
                          </span>
                          {key.is_active && (
                            <button
                              onClick={() => setRevokeTarget(key.id)}
                              className="text-xs text-red-500 hover:underline"
                            >
                              Revoke
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* Quick API reference */}
            <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 p-6">
              <h2 className="font-semibold text-gray-900 dark:text-white mb-2">
                Quick API reference
              </h2>
              <p className="text-gray-400 text-sm mb-4">
                Use your API key in the{" "}
                <code className="bg-gray-100 dark:bg-brand-dark-bg px-1.5 py-0.5 rounded text-xs">
                  X-API-Key
                </code>{" "}
                header.
              </p>
              <div className="flex flex-col gap-3">
                {[
                  {
                    method: "POST",
                    path: "/api/v1/employer/integrate",
                    desc: "Link a candidate (connect_token, resume_slug, or seeker_email)",
                    body: `{ "connect_token": "abc123xyz", "candidate_ref": "your-internal-id" }`,
                  },
                  {
                    method: "POST",
                    path: "/api/v1/employer/status-push",
                    desc: "Push a status update with optional correspondence actions",
                    body: `{ "application_id": "uuid", "status": "Interview", "note": "...", "actions": [{ "action_type": "meeting", "title": "First interview", "meeting_at": "2026-05-05T10:00:00Z", "duration_minutes": 45, "attendees": ["Jane Smith (HR)"] }] }`,
                  },
                ].map((endpoint) => (
                  <div
                    key={endpoint.path}
                    className="bg-gray-50 dark:bg-brand-dark-bg rounded-xl p-4"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-bold text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400 px-2 py-0.5 rounded">
                        {endpoint.method}
                      </span>
                      <code className="text-xs text-gray-600 dark:text-gray-300">
                        {endpoint.path}
                      </code>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      {endpoint.desc}
                    </p>
                    <code className="text-xs text-gray-500 dark:text-gray-500 break-all">
                      {endpoint.body}
                    </code>
                  </div>
                ))}
              </div>
              <button
                onClick={() => navigate("/employers/api")}
                className="mt-4 text-xs text-brand-violet dark:text-brand-violet-on-dark hover:underline"
              >
                Full API documentation →
              </button>
              {revokeTarget && (
                <>
                  <div
                    className="fixed inset-0 bg-black/40 z-40"
                    onClick={() => setRevokeTarget(null)}
                  />
                  <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
                    <div className="bg-white dark:bg-brand-dark-surface rounded-2xl border border-gray-100 dark:border-white/10 shadow-2xl w-full max-w-sm p-6">
                      <div className="flex items-start gap-4 mb-5">
                        <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                          <svg
                            className="w-5 h-5 text-red-500"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white mb-1">
                            Revoke API key?
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                            This will immediately disable the key. Any
                            integrations using it will stop working. This cannot
                            be undone.
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setRevokeTarget(null)}
                          className="flex-1 border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-300 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-white/5 transition"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleRevokeKey}
                          className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 rounded-lg text-sm font-medium transition"
                        >
                          Revoke key
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
